import { notification } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { QUERY_KEYS } from '@api'
import { AxiosErrorType } from '@types'

import RailcarApi from './api'

const { post } = RailcarApi
interface UseCreateRailcarProps {
  resetForm: (close?: boolean) => void
}

const useCreateRailcar = ({ resetForm }: UseCreateRailcarProps) => {
  const { t } = useTranslation()
  const [createRailcarIsFetching, setCreateRailcarIsFetching] = useState<boolean>()

  const queryClient = useQueryClient()

  const onSuccess = () => {
    notification.success({
      message: 'New railcar created successfully',
    })
    queryClient.invalidateQueries(QUERY_KEYS.BLOCKTRAIN)
    resetForm(true)
    setCreateRailcarIsFetching(false)
  }

  const onError = (err: AxiosErrorType) => {
    setCreateRailcarIsFetching(false)
    if (err.status === 404) {
      console.error('Route not found', { err })
    }
    const errorResponse = err.data.error as {
      type: string
      status: number
      message: string
    }

    if (errorResponse.type === 'NO_ACTIVE_ROUTE') {
      const hint =
        errorResponse.message.length > 62
          ? `${t('no_active_route_hint')} ${errorResponse.message.substring(
              62,
              errorResponse.message.length,
            )}`
          : `${t('no_active_route_at_all_hint')}`

      notification.error({
        message: `${t('no_active_route')} ${hint}`,
      })
    } else if (errorResponse.type === 'INVALID_FILE_SIZE') {
      notification.error({
        message: t('file_size_error'),
      })
    } else if (errorResponse.message.includes('duplicate')) {
      notification.error({
        message: t('created_error'),
      })
    } else {
      notification.error({
        message: t('no_created_railcar'),
      })
    }
  }

  const { mutate: createRailcar } = useMutation(post, {
    onSuccess,
    onError,
    onMutate: () => {
      setCreateRailcarIsFetching(true)
    },
  })

  return {
    createRailcar,
    createRailcarIsFetching,
  }
}

export { useCreateRailcar }
